import { Component, Input, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
    FEATURE_ORGANISATION_RELATIONSHIPS,
    FlyFreelyError,
    FlyFreelyLoggingService,
    LicenceDto,
    OrganisationSubscriptionDto,
    PersonService,
    PersonWithRoles,
    PersonsOrganisationDto,
    SubscriptionLimitDto,
    SubscriptionService,
    WorkTracker,
    hasFeatureFlag
} from '@flyfreely-portal-ui/flyfreely';
import {
    NgSelectPaginationValues,
    NgSelectSearch,
    ngSelectPreSearchConfigure,
    updateNgSelectData
} from '@flyfreely-portal-ui/ng-select';
import { personSearch } from '@flyfreely-portal-ui/ui';
import {
    Observable,
    Subject,
    catchError,
    debounceTime,
    finalize,
    from,
    of
} from 'rxjs';
import { concatMap, filter, map, startWith, takeUntil } from 'rxjs/operators';
import { OrganisationSubscriptionState } from './organisation-subscription-state.service';

@Component({
    selector: 'subscription-licences',
    template: `
        <div *ngIf="licenceLimit != null">
            {{ licenceLimit.availableLicenceCount }} of
            {{ licenceLimit.instances }} available ({{ assignedCount }} assigned
            {{ licenceLimit.inviteInstancesCount }} invites<ng-container
                *ngIf="canUseOrganisationRelationships"
            >
                {{ licenceLimit.sharedInstancesCount }} shared</ng-container
            >)
        </div>
        <div [formGroup]="formGroup">
            <div class="input-group">
                <ng-select
                    [items]="availablePersonnel"
                    bindValue="id"
                    appendTo="body"
                    formControlName="personIds"
                    placeholder="Select personnel"
                    [readonly]="working"
                    [loading]="loadingPersonnel"
                    [searchFn]="personSearch"
                    [multiple]="true"
                    [maxSelectedItems]="remainingLicences"
                    [virtualScroll]="true"
                    (scrollToEnd)="onPersonnelSelectScroll()"
                    (search)="onPersonnelSelectSearch($event)"
                    (close)="onPersonnelSelectClose()"
                    (open)="onPersonSelectOpen()"
                >
                    <ng-template ng-option-tmp let-item="item">
                        {{ item | formatPerson: true }}
                    </ng-template>
                    <ng-template ng-label-tmp let-item="item" let-clear="clear">
                        <span
                            class="ng-value-icon left"
                            (click)="clear(item)"
                            aria-hidden="true"
                            >×</span
                        >
                        <span class="ng-value-label">{{
                            item | formatPerson: true
                        }}</span>
                    </ng-template>
                </ng-select>
                <span class="input-group-btn">
                    <button
                        type="button"
                        class="btn btn-primary"
                        [disabled]="
                            working ||
                            !formGroup.valid ||
                            (remainingLicences != null &&
                                remainingLicences <= 0)
                        "
                        (click)="assignLicence()"
                    >
                        Add
                    </button>
                </span>
            </div>
            <div
                *ngFor="let licence of activeLicences$ | async"
                class="horizontal-container data "
            >
                <div
                    *ngIf="
                        (licence.person.firstName != 'Unknown' &&
                            licence.person.lastName != 'Person') ||
                        !loadingPersonnel
                    "
                    class="fill"
                >
                    {{ licence.person | formatPerson }} &lt;{{
                        licence.person.email
                    }}&gt;
                </div>
                <div
                    *ngIf="
                        (licence.person == null ||
                            (licence.person.firstName === 'Unknown' &&
                                licence.person.lastName === 'Person') ||
                            licence.person.firstName == null) &&
                        loadingPersonnel
                    "
                    class="fill"
                >
                    <em>Loading...</em>
                </div>
                <button
                    *ngIf="
                        (licence.person.firstName != 'Unknown' &&
                            licence.person.lastName != 'Person') ||
                        !loadingPersonnel
                    "
                    class="btn btn-tertiary"
                    type="button"
                    (click)="unassignLicence(licence.person.id)"
                    [disabled]="working"
                >
                    <span class="fal fa-trash-alt"></span>
                </button>
            </div>
        </div>
    `
})
export class SubscriptionLicences {
    @Input() organisation: PersonsOrganisationDto;
    @Input() subscription: OrganisationSubscriptionDto;

    subscriptionId: number;

    licenceLimit: SubscriptionLimitDto | undefined;

    remainingLicences: number;
    assignedCount: number;

    canUseOrganisationRelationships: boolean;

    private ngUnsubscribe$ = new Subject<void>();
    private workTracker = new WorkTracker();
    working = false;

    formGroup = new FormGroup({
        personIds: new FormControl<number[]>([], [Validators.required])
    });

    availablePersonnel: PersonWithRoles[] = [];
    loadingPersonnel = false;

    private assignedPersonnelLookup: { [personId: number]: PersonWithRoles } =
        {};

    private personnelSearchSource = new Subject<NgSelectSearch>();
    private personnelSelectConfig: NgSelectPaginationValues = {
        currentPage: 0,
        preSearchPage: null,
        searchValue: null,
        totalItems: null
    };

    private preloadedPersonIds = new Set<number>();

    activeLicences$: Observable<
        { person: PersonWithRoles; licence: LicenceDto }[]
    >;

    subscriptionUpdated$ = new Subject<void>();

    personSearch = personSearch.bind(this);

    constructor(
        private state: OrganisationSubscriptionState,
        private subscriptionService: SubscriptionService,
        private personnelService: PersonService,
        private logging: FlyFreelyLoggingService
    ) {
        this.workTracker.observable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => (this.working = working));

        this.personnelSearchSource
            .pipe(debounceTime(300), takeUntil(this.ngUnsubscribe$))
            .subscribe(search => {
                this.personnelSelectConfig = ngSelectPreSearchConfigure(
                    search,
                    this.personnelSelectConfig
                );
                this.refreshAvailablePersonnel();
            });

        this.activeLicences$ = this.subscriptionUpdated$
            .pipe(startWith(null))
            .pipe(
                filter(() => this.subscription !== undefined),
                map(() => {
                    if (
                        this.subscription == null ||
                        this.licenceLimit == null
                    ) {
                        return [];
                    }

                    return this.subscription.activeLicences
                        .filter(
                            l =>
                                l.entityType ===
                                    this.licenceLimit.licencedEntityType &&
                                l.endTime == null
                        )
                        .map(l => ({
                            person:
                                this.assignedPersonnelLookup[l.entityId] ??
                                ({
                                    id: l.entityId,
                                    firstName: 'Unknown',
                                    lastName: 'User'
                                } as PersonWithRoles),
                            licence: l
                        }));
                })
            );
    }

    ngOnInit() {
        this.loadingPersonnel = true;
        this.canUseOrganisationRelationships = hasFeatureFlag(
            this.organisation,
            FEATURE_ORGANISATION_RELATIONSHIPS
        );

        this.refreshSubscriptionDetails();
    }

    ngOnChanges(changes: SimpleChanges) {
        if ('subscription' in changes) {
            this.refreshSubscriptionDetails();

            if (this.organisation?.id && this.subscription) {
                this.preloadAssignedPersonnel();
            }
        }
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
        this.subscriptionUpdated$.complete();
        this.personnelSearchSource.complete();
    }

    private refreshSubscriptionDetails() {
        this.subscriptionId = this.subscription?.id;
        this.licenceLimit = this.subscription?.limits[0];
        this.assignedCount = this.subscription.activeLicences.length;
        this.remainingLicences =
            this.subscription?.limits[0]?.availableLicenceCount ?? null;
        this.subscriptionUpdated$.next();
    }

    /**
     * Preload assigned personnel using idList
     * This ensures we have complete data for the lookup
     */
    private preloadAssignedPersonnel() {
        if (
            this.organisation?.id == null ||
            this.subscription == null ||
            this.licenceLimit == null
        ) {
            return;
        }

        // Get assigned person IDs that haven't been preloaded yet
        const assignedPersonIds = this.subscription.activeLicences
            .filter(
                l =>
                    l.entityType === this.licenceLimit.licencedEntityType &&
                    l.endTime == null
            )
            .map(l => l.entityId)
            .filter(id => !this.preloadedPersonIds.has(id));

        if (assignedPersonIds.length === 0) {
            this.loadingPersonnel = false;
            return;
        }

        // Load assigned personnel into separate lookup
        this.loadAssignedPersonnel(assignedPersonIds);
    }

    /**
     * Load assigned personnel into assignedPersonnelLookup
     * Separate from availablePersonnel
     */
    private loadAssignedPersonnel(personIds: number[]) {
        if (personIds.length === 0) {
            return;
        }

        this.loadingPersonnel = true;

        this.personnelService
            .findPersonnelGraphQL(
                this.organisation.id,
                0,
                Math.max(personIds.length, 1), // Load all at once
                {
                    idList: personIds
                }
            )
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: results => {
                    // Update the assigned personnel lookup
                    results.results.forEach(person => {
                        this.assignedPersonnelLookup[person.id] = person;

                        // Mark these IDs as preloaded
                        this.preloadedPersonIds.add(person.id);
                    });

                    // Trigger activeLicences$ update
                    this.subscriptionUpdated$.next();
                    this.loadingPersonnel = false;
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error loading assigned personnel: ${error.message}`
                    );
                    this.loadingPersonnel = false;
                }
            })
            .add(this.workTracker.createTracker());
    }

    private refreshAvailablePersonnel() {
        if (this.organisation?.id == null) {
            return;
        }

        this.loadingPersonnel = true;

        // Build excludedIds from current active licences
        const excludedIds =
            this.subscription && this.licenceLimit
                ? this.subscription.activeLicences
                      .filter(
                          l =>
                              l.entityType ===
                                  this.licenceLimit.licencedEntityType &&
                              l.endTime == null
                      )
                      .map(l => l.entityId)
                : undefined;

        this.personnelService
            .findPersonnelGraphQL(
                this.organisation.id,
                this.personnelSelectConfig.currentPage,
                15,
                {
                    excludedIdList:
                        excludedIds && excludedIds.length > 0
                            ? excludedIds
                            : undefined,
                    textSearch:
                        this.personnelSelectConfig.searchValue || undefined
                }
            )
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: results => {
                    const newPersonnel = results.results.filter(
                        p =>
                            this.availablePersonnel.findIndex(
                                person => person.id === p.id
                            ) === -1
                    );
                    this.availablePersonnel =
                        this.availablePersonnel.concat(newPersonnel);

                    this.personnelSelectConfig = updateNgSelectData(
                        results,
                        this.personnelSelectConfig
                    );

                    this.loadingPersonnel = false;
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error loading personnel: ${error.message}`
                    );
                    this.loadingPersonnel = false;
                }
            })
            .add(this.workTracker.createTracker());
    }

    onPersonnelSelectScroll() {
        if (
            this.personnelSelectConfig.totalItems == null ||
            this.availablePersonnel.length <
                this.personnelSelectConfig.totalItems
        ) {
            this.refreshAvailablePersonnel();
        }
    }

    onPersonnelSelectSearch(search: NgSelectSearch) {
        this.personnelSearchSource.next(search);
    }

    onPersonnelSelectClose() {
        this.personnelSelectConfig.searchValue = null;
    }

    onPersonSelectOpen() {
        this.resetPersonnelList();
    }

    assignLicence() {
        const selectedPersonIds = this.formGroup.value.personIds || [];

        if (selectedPersonIds.length > this.remainingLicences) {
            this.logging.error(
                null,
                `You can only assign ${this.remainingLicences} licence(s). You have selected ${selectedPersonIds.length} user(s).`
            );
            return;
        }

        let successCount = 0;
        const totalCount = selectedPersonIds.length;

        from(selectedPersonIds)
            .pipe(
                concatMap(personId =>
                    this.subscriptionService
                        .assignLicence(this.subscriptionId, {
                            entityId: personId,
                            entityType: this.licenceLimit.licencedEntityType
                        })
                        .pipe(
                            map(() => {
                                successCount++;
                                return { success: true };
                            }),
                            catchError((error: FlyFreelyError) => {
                                this.logging.error(
                                    error,
                                    `Error while assigning licence: ${error.message}`
                                );
                                return of({ success: false });
                            })
                        )
                ),
                finalize(() => {
                    if (successCount > 0) {
                        this.logging.success(
                            `${successCount} of ${totalCount} licence${
                                totalCount > 1 ? 's' : ''
                            } assigned`
                        );
                    }
                    this.state.refresh(this.organisation.id);
                    this.formGroup.reset();
                    this.resetPersonnelList();
                }),
                takeUntil(this.ngUnsubscribe$)
            )
            .subscribe()
            .add(this.workTracker.createTracker());
    }

    unassignLicence(personId: number) {
        this.subscriptionService
            .unassignLicence(this.subscriptionId, {
                entityId: personId,
                entityType: this.licenceLimit.licencedEntityType
            })
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: subscription => {
                    this.logging.success(`Licence unassigned`);
                    this.state.change(subscription);
                    this.subscription = subscription;
                    this.refreshSubscriptionDetails();
                    this.resetPersonnelList();
                },
                error: (error: FlyFreelyError) =>
                    this.logging.error(
                        error,
                        `Error while unassigning licence: ${error.message}`
                    )
            })

            .add(this.workTracker.createTracker());
    }

    private resetPersonnelList() {
        this.availablePersonnel = [];
        this.preloadedPersonIds.clear();
        this.personnelSelectConfig = {
            currentPage: 0,
            preSearchPage: null,
            searchValue: null,
            totalItems: null
        };

        this.preloadAssignedPersonnel();
        this.refreshAvailablePersonnel();
    }
}
